import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileArrowUp, FileText, CloudArrowUp, Folder } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import api from '../services/api';

export function PortalDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadDossierId, setUploadDossierId] = useState('');
  const [uploadType, setUploadType] = useState('autre');
  const fileRef = useRef(null);

  const fetchData = async () => {
    try {
      const [docsRes, dossiersRes] = await Promise.all([
        api.get('/portal/documents'),
        api.get('/portal/dossiers'),
      ]);
      setDocuments(docsRes.data.items || []);
      setDossiers(dossiersRes.data.items || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type_document', uploadType);
      if (uploadDossierId) formData.append('dossier_id', uploadDossierId);

      await api.post('/portal/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document envoye avec succes');
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadDossierId('');
      setUploadType('autre');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Erreur upload'));
    } finally {
      setUploading(false);
    }
  };

  const typeDocOptions = [
    { value: 'statuts', label: 'Statuts' },
    { value: 'kbis', label: 'Extrait Kbis' },
    { value: 'bilan', label: 'Bilan comptable' },
    { value: 'pv_ag', label: 'PV AG' },
    { value: 'rib', label: 'RIB' },
    { value: 'contrat', label: 'Contrat / Convention' },
    { value: 'facture', label: 'Facture' },
    { value: 'attestation', label: 'Attestation' },
    { value: 'cv', label: 'CV' },
    { value: 'autre', label: 'Autre' },
  ];

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="portal-documents-title">Mes documents</h1>
          <p className="text-[#5E6D66] mt-1 text-sm">Envoyez et suivez vos documents</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="portal-upload-btn">
          <CloudArrowUp size={18} className="mr-2" /> Envoyer un document
        </Button>
      </div>

      {/* Documents table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {documents.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="mx-auto text-[#E8E6E1] mb-4" />
              <p className="text-[#5E6D66]">Aucun document</p>
              <Button onClick={() => setUploadOpen(true)} variant="link" className="text-[#2D5A4C] mt-2">Envoyer votre premier document</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E8E6E1]">
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Document</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Dossier</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Taille</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-[#5E6D66]">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => {
                  const dossier = dossiers.find(d => d.id === doc.dossier_id);
                  return (
                    <TableRow key={doc.id} className="border-[#E8E6E1]" data-testid={`portal-doc-row-${doc.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-[#2D5A4C]" />
                          <span className="text-sm font-medium text-[#1A2421]">{doc.titre || doc.original_filename}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{doc.type_document}</Badge>
                      </TableCell>
                      <TableCell>
                        {dossier ? (
                          <span className="text-xs text-[#5E6D66] flex items-center gap-1"><Folder size={12} />{dossier.numero_dossier}</span>
                        ) : <span className="text-xs text-[#5E6D66]">-</span>}
                      </TableCell>
                      <TableCell className="text-xs text-[#5E6D66]">{formatSize(doc.size)}</TableCell>
                      <TableCell className="text-xs text-[#5E6D66]">{doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell>
                        <Badge className={doc.statut === 'recu' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}>
                          {doc.statut === 'recu' ? 'Recu' : doc.statut}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Envoyer un document</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fichier *</Label>
              <div
                className="border-2 border-dashed border-[#E8E6E1] rounded-lg p-6 text-center cursor-pointer hover:border-[#2D5A4C] transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="portal-file-dropzone"
              >
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => setSelectedFile(e.target.files[0])} />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileArrowUp size={20} className="text-[#2D5A4C]" />
                    <span className="text-sm text-[#1A2421] font-medium">{selectedFile.name}</span>
                    <span className="text-xs text-[#5E6D66]">({formatSize(selectedFile.size)})</span>
                  </div>
                ) : (
                  <div>
                    <CloudArrowUp size={32} className="mx-auto text-[#E8E6E1] mb-2" />
                    <p className="text-sm text-[#5E6D66]">Cliquez pour selectionner un fichier</p>
                    <p className="text-xs text-[#5E6D66] mt-1">PDF, Word, Excel, images</p>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type de document</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="portal-upload-type"><SelectValue /></SelectTrigger>
                <SelectContent>{typeDocOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Dossier associe</Label>
              <Select value={uploadDossierId} onValueChange={setUploadDossierId}>
                <SelectTrigger data-testid="portal-upload-dossier"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {dossiers.map(d => <SelectItem key={d.id} value={d.id}>{d.numero_dossier}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annuler</Button>
            <Button onClick={handleUpload} className="bg-[#2D5A4C] hover:bg-[#1E4136]" disabled={!selectedFile || uploading} data-testid="portal-upload-submit">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CloudArrowUp size={16} className="mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
